import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import {
  LuAudioLines,
  LuLayers,
  LuLoaderCircle,
  LuShieldCheck,
  LuTimer,
  LuTriangleAlert,
  LuVideo,
} from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
import "./_legacyTimelineRepairModal.scss";

const t = (key, options) => i18n.t(`legacy_timeline_repair.${key}`, options);

/**
 * Shown right after a pre-timeline training is loaded: its video/audio boxes
 * carry no saved duration, which shortens scenes and drops their sound from a
 * video export (see _editor/utils/mediaTimelineRepair). The copy stays at the
 * "what you get" level; the duration mechanics live in that module.
 */
export default function LegacyTimelineRepairModal({ show, summary, onRepair, onDismiss }) {
  const [phase, setPhase] = useState("idle"); // idle | running | error
  const [progress, setProgress] = useState(null);

  // A fresh open starts clean: a failed attempt must not leak into the next one.
  useEffect(() => {
    if (!show) {
      setPhase("idle");
      setProgress(null);
    }
  }, [show]);

  const run = async () => {
    setPhase("running");
    setProgress(null);
    try {
      await onRepair(setProgress);
      // The caller hides the dialog on success; this only keeps a caller that
      // does not from leaving the buttons locked on "Updating…".
      setPhase("idle");
      setProgress(null);
    } catch (e) {
      setPhase("error");
      setProgress(null);
    }
  };

  const running = phase === "running";
  const failed = phase === "error";

  const { videos = 0, audios = 0, scenes = 0 } = summary || {};
  const stats = [
    { key: "videos", value: videos, Icon: LuVideo },
    { key: "audios", value: audios, Icon: LuAudioLines },
    { key: "scenes", value: scenes, Icon: LuLayers },
  ].filter((stat) => stat.value > 0);

  // Progress counts probed media files; once they are all checked the only
  // work left is the save, so the label switches to say so.
  const hasProgress = progress && progress.total > 0;
  const percent = hasProgress ? Math.round((progress.done / progress.total) * 100) : 0;
  let progressText = t("preparing");
  if (hasProgress) {
    progressText =
      progress.done >= progress.total
        ? t("saving")
        : t("progress", { done: progress.done, total: progress.total });
  }

  let confirmLabel = t("repair");
  if (running) {
    confirmLabel = (
      <>
        <LuLoaderCircle className="legacy-repair-modal__spinner" size={16} aria-hidden="true" />
        {t("running")}
      </>
    );
  } else if (failed) {
    confirmLabel = t("retry");
  }

  return (
    <AppModal
      show={show}
      size="md"
      maxWidth={520}
      centered
      closeButton={false}
      showSceneToggle={false}
      className="legacy-repair-modal"
      onClose={running ? () => {} : onDismiss}
      title={t("title")}
      cancelLabel={t("later")}
      confirmLabel={confirmLabel}
      onCancel={onDismiss}
      onConfirm={run}
      cancelDisabled={running}
      confirmDisabled={running}
    >
      <div className="legacy-repair-modal__lead">
        <div className="legacy-repair-modal__icon-wrap" aria-hidden="true">
          <LuTimer size={22} />
        </div>
        <p className="legacy-repair-modal__text">{t("body")}</p>
      </div>

      {stats.length > 0 && (
        <div className="legacy-repair-modal__stats" role="list">
          {stats.map(({ key, value, Icon }) => (
            <div key={key} className="legacy-repair-modal__stat" role="listitem">
              <Icon className="legacy-repair-modal__stat-icon" size={16} aria-hidden="true" />
              <span className="legacy-repair-modal__stat-value">{value}</span>
              <span className="legacy-repair-modal__stat-label">{t(key, { count: value })}</span>
            </div>
          ))}
        </div>
      )}

      <p className="legacy-repair-modal__note">
        <LuShieldCheck size={16} aria-hidden="true" />
        <span>{t("note")}</span>
      </p>

      {running && (
        <div
          className={
            hasProgress
              ? "legacy-repair-modal__progress"
              : "legacy-repair-modal__progress legacy-repair-modal__progress--indeterminate"
          }
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={hasProgress ? percent : undefined}
          aria-label={progressText}
        >
          <div className="legacy-repair-modal__progress-track">
            <div className="legacy-repair-modal__progress-fill" style={{ width: `${percent}%` }} />
          </div>
          <span className="legacy-repair-modal__progress-text">{progressText}</span>
        </div>
      )}

      {failed && (
        <div className="legacy-repair-modal__error" role="alert">
          <LuTriangleAlert size={16} aria-hidden="true" />
          <span>{t("error")}</span>
        </div>
      )}
    </AppModal>
  );
}

LegacyTimelineRepairModal.propTypes = {
  show: PropTypes.bool.isRequired,
  /** What the update touches. Zero counts are left out of the dialog. */
  summary: PropTypes.shape({
    videos: PropTypes.number,
    audios: PropTypes.number,
    scenes: PropTypes.number,
  }),
  /** async (onProgress) => void — repairs and saves; closing is the caller's job. */
  onRepair: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};
